import { FC } from 'react';
import { AutoComplete, Form, Typography } from 'antd';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import { EnumReportTypes } from '../../../../../types/reports';
import { formatAmount } from '../../../../../utils/formatAmount';
import { useContextReports } from '../../../../../context/reports';
import styles from './TotalCashToCloseCol.module.scss'
import { toFixed, toFloat } from '../../../../../utils/text';
import { calculateLoanToValue } from '../../../../../utils/calculateLoanToValue';
import { calculateUip } from '../../../../../utils/loan';
import { calculateLoanClosingCostAmount } from '../../../../../utils/calculateLoanClosingCostAmount';
import { calculateAPR } from '../../../../../utils/calculateAPR';
import { calculateTotalCashToClose } from '../../../../../utils/calculateTotalCashToClose';
import { calculateVaFundingFee } from '../../../../../utils/calculateVaFundingFee';
import { calculateClosingCosts } from '../../../../../utils/calculateClosingCosts';
import { calculateLoanAmountWithUip } from '../../../../../utils/calculateLoanAmountWithUip';
import { calculateDownPaymentPrice } from '../../../../../utils/calculateDownPaymentPrice';
import { calculatePrincipalInterest } from '../../../../../utils/calculatePrincipalInterest';
import { calculateDownPaymentPercentage } from '../../../../../utils/calculateDownPaymentPercentage';
import { calculateLoanAmount } from '../../../../../utils/calculateLoanAmount';
import { calculateDepositMoneyPrice } from '../../../../../utils/calculateDepositMoneyPrice';
import { calculatePmi } from '../../../../../utils/calculatePmi';
import { calculatePmiPercentage } from '../../../../../utils/calculatePmiPercentage';

type Props = {
  loan: any;
  index: number;
}

const EARNEST_MONEY_DEPOSIT_PERCENTAGES = [0.5, 1, 1.5, 2, 2.5, 3];

const TotalCashToCloseCol: FC<Props> = ({ loan, index }) => {
  const { report, reportClosingCosts, reportLoanClosingCosts, isBorrower, handleLoansUpdate } = useContextReports();

  const form = Form.useFormInstance()

  const downPaymentActive = form?.getFieldValue(['tableView', 'downPayment', 'active']);
  const closingCostTimelineActive = form?.getFieldValue(['reportView', 'closingCostsEstimates', 'active']);
  const purchasePrice = parseFloat(loan?.purchasePrice?.value) || 0;
  const loanAmount = parseFloat(loan?.loanAmount?.value) || 0;
  const downPayment = parseFloat(loan?.downPayment?.value) || 0;
  const closingCosts = parseFloat(loan?.closingCosts?.value) || 0;
  const depositMoneyPrice = parseFloat(loan?.deposit_money_price) || 0;
  const addClosingCosts = loan?.externalLoan?.addClosingCosts;
  const removeDownPayment = form.getFieldValue(['loans', index, 'externalLoan', 'removeDownPayment']);
  const rentActive = form.getFieldValue(['tableView', 'rent', 'active']);
  const rent = form.getFieldValue(['loans', index, 'externalLoan', 'rent'])

  const shouldShowDash = rentActive && rent;

  const totalCashToClose = calculateTotalCashToClose(loan, form)

  const address = loan?.address?.toUpperCase();
  const loanName = loan?.name;
  const isFirst = index === 0;
  const isRefinance = report?.reportType === EnumReportTypes.refinance || false;

  let name: string

  if (isRefinance && isFirst) {
    name = 'Current Loan';
  } else if (isRefinance) {
    const defaultName = `Refi Option ${index}`;

    name = loanName || defaultName;
  } else {
    const defaultName = `Option ${index + 1}`;

    name = loanName || defaultName;
  }

  let delimiter = '';

  if (address?.includes(';')) {
    delimiter = ';';
  } else if (address?.includes(',')) {
    delimiter = ',';
  }

  const address1 = delimiter ? address?.split(delimiter)[0] : address;
  const address2 = delimiter ? address?.replace(`${address1}${delimiter}`, '') : null;

  // const handleIsAllowed = (values: NumberFormatValues) =>
  //   !values?.value.includes('.');

  const handleBlurDownPayment = (event: any) => {
    const rawValue = event?.target?.value ?? "";
    const value = parseFloat(rawValue.replace(/[^0-9.]/g, "")) || 0;
    const uipActive = form.getFieldValue(['tableView', 'uip', 'active']);
    const vaFundingFeeActive = form.getFieldValue(['tableView', 'vaFundingFee', 'active']);
    const loanPointsActive = form.getFieldValue(['tableView', 'loanPoints', 'active']);
    const interestRate = parseFloat(form.getFieldValue(['loans', index, 'interestRate', 'value'])) || 0;
    const loanPeriodInYears = parseFloat(form.getFieldValue(['loans', index, 'loanPeriodInYears', 'value'])) || 0;
    const closingCostsClearedAt = form.getFieldValue(['loans', index, 'externalLoan', 'closingCostsClearedAt']);

    form.setFieldValue(['loans', index, 'downPayment', 'value'], value);

    const downPaymentPercentage = calculateDownPaymentPercentage(form, index, { downPayment: value });

    form.setFieldValue(['loans', index, 'downPaymentPercentage', 'value'], downPaymentPercentage);

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const loanAmount = calculateLoanAmount(form, index, { downPayment: value });

    form.setFieldValue(['loans', index, 'loanAmount', 'value'], loanAmount);

    const pmi = calculatePmi(form, index);

    form.setFieldValue(['loans', index, 'pmi', 'value'], pmi);

    const pmiPercentage = calculatePmiPercentage(form, index);

    form.setFieldValue(['loans', index, 'externalLoan', 'pmiPercentage'], pmiPercentage);

    const loanToValue = calculateLoanToValue(form, index);

    form.setFieldValue(['loans', index, 'externalLoan', 'loanToValue'], loanToValue)

    const uip = calculateUip(loanAmount);

    form.setFieldValue(['loans', index, 'uip', 'value'], uip);

    const vaFundingFeePercentage = form.getFieldValue(['loans', index, 'externalLoan', 'vaFundingFeePercentage']) || 0;
    const vaFundingFee = calculateVaFundingFee(loanAmount, vaFundingFeePercentage);

    const loanClosingCost = reportLoanClosingCosts?.filter(cost => cost.loanId === loan?.id);

    const totalClosingCosts = calculateClosingCosts(
      form,
      index,
      reportLoanClosingCosts,
      reportClosingCosts,
      { uip, vaFundingFee }
    )

    const loanAmountWithUip = calculateLoanAmountWithUip(
      form,
      index,
      { uip, vaFundingFee, closingCosts: totalClosingCosts }
    )

    form.setFieldValue(['loans', index, 'loanAmountWithUip', 'value'], loanAmountWithUip);

    const downPaymentPrice = formatAmount(value + totalClosingCosts)

    form.setFieldValue(['loans', index, 'down_payment_price'], downPaymentPrice)
    form.setFieldValue(['loans', index, 'closingCosts', 'value'], totalClosingCosts)

    const updatedLoan = form.getFieldValue(['loans', index]);

    const totalAprRelatedClosingCost = loanClosingCost?.reduce((sum, closingCost) => {
      const reportClosingCost = reportClosingCosts?.find(c => c.name === closingCost.name);

      if (closingCost.name === 'uip' && uipActive) {
        return sum + uip
      }

      if (closingCost.name === 'vaFundingFee' && vaFundingFeeActive) {
        return sum + vaFundingFee
      }

      if (closingCost.name === 'loan_points' && loanPointsActive) {
        return sum + closingCost.amount
      }

      if (reportClosingCost?.aprIncluded && reportClosingCost?.active && !closingCostsClearedAt) {
        const amount = calculateLoanClosingCostAmount(
          updatedLoan,
          closingCost.type,
          reportClosingCost?.amount ?? 0,
          closingCost.propertyTaxesMonthsCollected,
          closingCost.daysCollected
        )

        return sum + amount;
      }

      return sum
    }, 0) ?? 0

    const normalizedAprClosingCost = parseFloat(formatAmount(Math.max(0, totalAprRelatedClosingCost)));

    const apr = calculateAPR(loanAmount, interestRate, loanPeriodInYears, normalizedAprClosingCost);

    form.setFieldValue(['loans', index, 'apr', 'value'], apr);
    form.setFieldValue(['loans', index, 'aprRelatedClosingCost', 'value'], normalizedAprClosingCost);

    const principalInterest = calculatePrincipalInterest(updatedLoan);

    form.setFieldValue(['loans', index, 'principalInterest', 'value'], principalInterest);

    if (downPayment !== value) {
      handleLoansUpdate?.();
    }
  }

  const handleBlurDepositMoneyPrice = (event: any) => {
    const rawValue = event?.target?.value ?? "";
    const value = parseFloat(rawValue.replace(/[^0-9.-]/g, "")) || 0;

    form.setFieldValue(['loans', index, 'deposit_money_price'], value)

    const downPaymentPrice = calculateDownPaymentPrice(form, index)

    form.setFieldValue(['loans', index, 'down_payment_price'], downPaymentPrice)

    if (depositMoneyPrice !== value) {
      handleLoansUpdate?.();
    }
  }

  return (
    (
      <div className={styles.tableHeadCol}>
        <div className={styles.tableHeadCell}>
          <Typography.Title level={4} className="color-gray8">
            {name}
          </Typography.Title>
          <Typography style={{ whiteSpace: 'normal', fontSize: '0.825rem' }} className="color-gray8">
            {address1 ? `${address1}` : ''}
          </Typography>
          <Typography style={{ whiteSpace: 'normal', fontSize: '0.825rem' }} className="color-gray8">
            {address2}
          </Typography>
        </div>
        <div>
          {downPaymentActive && (
            // eslint-disable-next-line no-nested-ternary
            shouldShowDash ? (
              <p className={styles.tableCell}>-</p>
            ) : isBorrower ? (
              <p className={styles.tableCell}>
                {formatAmount(downPayment, { showCurrency: true })}
              </p>
            ) : (
              <NumericFormat
                defaultValue={formatAmount(downPayment)}
                thousandSeparator
                onBlur={handleBlurDownPayment}
                prefix="$"
                placeholder="$0"
                // isAllowed={handleIsAllowed}
                className={styles.input}
                disabled={removeDownPayment}
              />
            )
          )}

          <p
            className={styles.tableCell}
            style={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center'
            }}
          >
            {shouldShowDash ? '-' : (
              <>
                {formatAmount(closingCosts, { showCurrency: true })}
                {addClosingCosts && <span className={styles.tableCellInfoText}>Included in loan amount</span>}
              </>
            )}
          </p>

          {closingCostTimelineActive && (
            // eslint-disable-next-line no-nested-ternary
            shouldShowDash ? (
              <p className={styles.tableCell}>-</p>
            ) : isBorrower ? (
              <p className={styles.tableCell}>
                {formatAmount(depositMoneyPrice, { showCurrency: true })}
              </p>
            ) : (
              <AutoComplete
                options={[
                  {
                    label: 'Percentage of purchase price',
                    options: EARNEST_MONEY_DEPOSIT_PERCENTAGES.map(percentage => ({
                      label: `${percentage}%`,
                      value: calculateDepositMoneyPrice(form, index, { depositMoneyPricePercentage: percentage })
                    }))
                  }
                ]}
                defaultValue={formatAmount(depositMoneyPrice)}
                onBlur={handleBlurDepositMoneyPrice}
              >
                <NumericFormat
                  thousandSeparator
                  prefix="($"
                  suffix=")"
                  placeholder="($0)"
                  // isAllowed={handleIsAllowed}
                  className={styles.input}
                />
              </AutoComplete>
            )
          )}

          {isRefinance && (
            <p className={styles.tableCell}>
              {shouldShowDash ? '-' : formatAmount(loanAmount, { showCurrency: true })}
            </p>
          )}

          <p className={styles.tableCell} style={{ borderTop: '2px solid var(--color-border)' }}>
            {shouldShowDash ? '-' : formatAmount(totalCashToClose, { showCurrency: true })}
          </p>
        </div>
      </div>
    )
  )
}

export default TotalCashToCloseCol;
